import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Card,
    Form,
    Input,
    Modal,
    Popconfirm,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import { PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import useDocumentTitle from "../hooks/useDocumentTitle";
import config from "../api/config";
import {useDispatch} from "react-redux";
import {resetMenu} from "../redux/slices/menuSlice";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faRemove} from "@fortawesome/free-solid-svg-icons";
import useAuth from "../hooks/useAuth";

/**
 * OrganizationsPage (API-driven)
 * - Поиск по названию/ИНН
 * - Таблица (server-side pagination)
 * - Добавление/Редактирование (Modal + Form)
 * - Удаление
 *
 * Ожидаемые ручки (пример):
 *  GET    /api/private/company?q=&page=&pageSize=  -> { items: [], total: number }
 *  POST   /api/private/company                     -> Organization
 *  PUT    /api/private/company/:id                 -> Organization
 *  DELETE /api/private/company/:id                 -> void
 */

// ====== Types (для TS можно вынести) ======
// Organization: { id: string; name: string; inn: string; createdAt?: string }

const http = axios.create({
    // baseURL: import.meta.env.VITE_API_URL, // если нужно
    baseURL: config.host + "/api",
    //withCredentials: true,
});

http.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    } else {
        delete config.headers.Authorization;
    }

    return config;
});

const organizationsApi = {
    async list({ q, page, pageSize }) {
        const res = await http.get("/private/company", {
            params: { q: q || undefined, page, pageSize },
        });
        return res.data; // { items, total }
    },
    async create(data) {
        const res = await http.post("/private/company/create", data);
        return res.data;
    },
    async update(id, data) {
        const res = await http.put(`/private/company/${id}`, data);
        return res.data;
    },
    async remove(id) {
        await http.delete(`/private/company/${id}`);
    },
};

const normalizeInn = (value) => (value ?? "").replace(/\s+/g, "");

const innRules = [
    { required: true, message: "Введите ИНН" },
    {
        validator: async (_, value) => {
            const v = normalizeInn(value);
            if (!v) return Promise.resolve();
            if (!/^\d+$/.test(v)) return Promise.reject(new Error("ИНН должен содержать только цифры"));
            if (![10, 12].includes(v.length)) {
                return Promise.reject(new Error("ИНН должен быть длиной 10 или 12 цифр"));
            }
            return Promise.resolve();
        },
    },
];

const OrganizationsPage = () => {
    useDocumentTitle("Организации");
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(resetMenu());
    }, [dispatch]);

    const { isAdmin , isAdminOrManagerOrPartner} = useAuth();

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);

    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");

    // server-side pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState("create"); // 'create' | 'edit'
    const [active, setActive] = useState(null);

    const [form] = Form.useForm();
    const abortRef = useRef({ aborted: false });

    const fetchList = useCallback(async () => {
        setLoading(true);
        abortRef.current.aborted = false;

        try {
            const data = await organizationsApi.list({ q: query, page, pageSize });
            if (!abortRef.current.aborted) {
                setItems(data?.items || []);
                setTotal(Number(data?.total || 0));
            }
        } catch (e) {
            if (!abortRef.current.aborted) {
                message.error(e?.message || "Не удалось загрузить список организаций");
            }
        } finally {
            if (!abortRef.current.aborted) setLoading(false);
        }
    }, [query, page, pageSize]);

    useEffect(() => {
        fetchList();
        return () => {
            abortRef.current.aborted = true;
        };
    }, [fetchList]);

    const openCreate = () => {
        setMode("create");
        setActive(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setMode("edit");
        setActive(record);
        form.setFieldsValue({
            name: record.name,
            inn: record.inn,
        });
        setModalOpen(true);
    };

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                name: values.name.trim(),
                inn: normalizeInn(values.inn),
            };

            if (mode === "create") {
                await organizationsApi.create(payload);
                message.success("Организация добавлена");
                // часто после добавления удобно вернуться на первую страницу
                setPage(1);
            } else {
                await organizationsApi.update(active.id, payload);
                message.success("Организация обновлена");
            }

            setModalOpen(false);
            await fetchList();
        } catch (e) {
            if (e?.errorFields) return; // это ошибки валидации формы
            message.error(e?.message || "Не удалось сохранить организацию");
        }
    };

    const onDelete = async (id) => {
        try {
            await organizationsApi.remove(id);
            message.success("Организация удалена");

            // если удалили последний элемент на странице — откатываемся назад
            if (items.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                await fetchList();
            }
        } catch (e) {
            message.error(e?.message || "Не удалось удалить организацию");
        }
    };

    const columns = useMemo(() => {
        const baseColumns = [
            {
                title: "ИНН",
                dataIndex: "inn",
                key: "inn",
                width: 180,
                render: (v) => <Tag>{v}</Tag>,
                responsive: ['md'],
            },
            {
                title: "Название",
                dataIndex: "name",
                key: "name",
                render: (v) => <Typography.Text strong>{v}</Typography.Text>,
                responsive: ['md'],
            },
        ];

        // Условная колонка
        if (isAdmin) {
            baseColumns.push({
                title: "Партнер",
                dataIndex: "partner",
                key: "partner",
                render: (v) => v ? v : "-",
                responsive: ['md'],
            });
        }

        // Колонка действий всегда последняя
        baseColumns.push({
            title: "Действия",
            key: "actions",
            width: 100,
            responsive: ['md'],
            render: (_, record) => (
                <Space>
                    <button
                        onClick={() => openEdit(record)}
                        className="btn h-8 w-8 p-0 text-info hover:bg-info/20 focus:bg-info/20 active:bg-info/25"
                    >
                        <FontAwesomeIcon icon={faEdit}/>
                    </button>

                    <Popconfirm
                        title="Удалить организацию?"
                        description="Действие нельзя отменить"
                        okText="Удалить"
                        okButtonProps={{ danger: true }}
                        cancelText="Отмена"
                        onConfirm={() => onDelete(record.id)}
                    >
                        <button className="btn h-8 w-8 p-0 text-error hover:bg-error/20 focus:bg-error/20 active:bg-error/25">
                            <FontAwesomeIcon icon={faRemove}/>
                        </button>
                    </Popconfirm>
                </Space>
            ),
        });

        baseColumns.push({
            title: 'Информация об организации',
            responsive: ['xs'],
            render: (_, record) => {
                return <>
                    <div><span className={'font-bold'}>ИНН:</span> {record.inn}</div>
                    <div><span className={'font-bold'}>Название:</span> {record.name}</div>
                    {
                        isAdmin &&
                        <div><span className={'font-bold'}>Партнер:</span> {record.partner ? record.partner : "-"}</div>

                    }
                    <div class={'flex justify-center items-center space-x-2 mt-2'}>
                        <button
                            onClick={() => openEdit(record)}
                            className="btn h-8 w-8 p-0 text-info hover:bg-info/20 focus:bg-info/20 active:bg-info/25"
                        >
                            <FontAwesomeIcon icon={faEdit}/>
                        </button>

                        <Popconfirm
                            title="Удалить организацию?"
                            description="Действие нельзя отменить"
                            okText="Удалить"
                            okButtonProps={{ danger: true }}
                            cancelText="Отмена"
                            onConfirm={() => onDelete(record.id)}
                        >
                            <button className="btn h-8 w-8 p-0 text-error hover:bg-error/20 focus:bg-error/20 active:bg-error/25">
                                <FontAwesomeIcon icon={faRemove}/>
                            </button>
                        </Popconfirm>
                    </div>
                </>
            }
        });

        return baseColumns;
    }, [isAdmin, items.length, page]);

    return (
        <div className="p-4">
            <Card
                className="rounded-2xl shadow-sm"
                title={
                    <div className="flex items-center justify-between gap-3 mob-flex-wrap mob-pb-4 mob-pt-2">
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Организации
                        </Typography.Title>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={fetchList} />
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                                Добавить
                            </Button>
                        </Space>
                    </div>
                }
            >
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <Input
                            allowClear
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setPage(1);
                            }}
                            onPressEnter={() => {
                                setPage(1);
                                fetchList();
                            }}
                            placeholder="Поиск по названию или ИНН"
                            prefix={<SearchOutlined />}
                            style={{ maxWidth: 420 }}
                        />
                        <Button
                            onClick={() => {
                                setPage(1);
                                fetchList();
                            }}
                        >
                            Найти
                        </Button>
                    </div>

                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={items}
                        pagination={{
                            current: page,
                            pageSize,
                            total,
                            showSizeChanger: true,
                            onChange: (nextPage, nextPageSize) => {
                                setPage(nextPage);
                                setPageSize(nextPageSize);
                            },
                        }}
                    />
                </div>
            </Card>

            <Modal
                open={modalOpen}
                title={mode === "create" ? "Добавить организацию" : "Редактировать организацию"}
                onCancel={() => setModalOpen(false)}
                onOk={onSubmit}
                okText={mode === "create" ? "Добавить" : "Сохранить"}
                cancelText="Отмена"
                destroyOnClose
            >
                <Form form={form} layout="vertical" preserve={false} initialValues={{ name: "", inn: "" }}>
                    <Form.Item
                        label="Название"
                        name="name"
                        rules={[{ required: true, message: "Введите название" }, { max: 255, message: "Слишком длинное" }]}
                    >
                        <Input placeholder='ООО "Ромашка"' />
                    </Form.Item>

                    <Form.Item label="ИНН" name="inn" rules={innRules}>
                        <Input inputMode="numeric" placeholder="10 или 12 цифр" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default OrganizationsPage;
